import React, { useEffect, useState } from "react";
import { api, formatDate, resolveBackendUrl } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Download } from "lucide-react";
import { isVeltaApk } from "@/lib/staffAccess";

function apiOriginBase() {
  const raw = String(resolveBackendUrl() || "").replace(/\/+$/, "");
  if (!raw) return "/api";
  return /\/api$/i.test(raw) ? raw : `${raw}/api`;
}

export default function Messages() {
  const [list, setList] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const load = () => api.get("/messages").then((r) => {
    const rows = Array.isArray(r.data) ? r.data : [];
    setList(rows);
    window.dispatchEvent(new Event("velta-messages-changed"));
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const open = async (msg) => {
    setOpenId(msg.id);
    if (msg.read_at) return;
    try {
      await api.post(`/messages/${msg.id}/read`);
      setList((rows) => rows.map((m) => (m.id === msg.id ? { ...m, read_at: new Date().toISOString() } : m)));
      window.dispatchEvent(new Event("velta-messages-changed"));
    } catch {
      /* il testo resta visibile anche se il segno non si aggiorna */
    }
  };

  const download = async (msg) => {
    setLoadingFile(true);
    try {
      if (isVeltaApk()) {
        const r = await api.post(`/messages/${msg.id}/file-link`);
        const url = `${apiOriginBase()}${r.data?.path || ""}`;
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url });
        return;
      }
      const r = await api.get(`/messages/${msg.id}/file`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = msg.filename || "documento.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF non disponibile");
    } finally {
      setLoadingFile(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-3xl">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Oggi</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
        <Mail className="w-8 h-8" /> Messaggi
      </h1>
      <p className="text-velta-mute mb-8">Comunicazioni di Velta Sistemi per questo locale.</p>

      <div className="space-y-2">
        {list.map((m) => {
          const unread = !m.read_at;
          const active = m.id === openId;
          return (
            <div
              key={m.id}
              className={`border rounded-md px-4 py-3 bg-white ${active ? "border-[#18181B]" : "border-velta-border"}`}
            >
              <button
                type="button"
                data-testid={`msg-${m.id}`}
                onClick={() => open(m)}
                className="w-full text-left flex items-center gap-2"
              >
                {unread && <span className="w-2 h-2 rounded-full bg-[#B73C27] shrink-0" aria-label="non letto" />}
                <span className="font-medium truncate">{m.title}</span>
                <span className="ml-auto text-xs text-velta-mute shrink-0">{formatDate(m.created_at)}</span>
              </button>
              {active && (
                <div className="mt-3 text-sm text-[#3F3F46] whitespace-pre-wrap">
                  {m.body || "Nessun testo."}
                  {m.has_file && (
                    <div className="mt-3">
                      <button
                        type="button"
                        data-testid={`msg-file-${m.id}`}
                        disabled={loadingFile}
                        onClick={() => download(m)}
                        className="inline-flex items-center gap-2 h-10 px-4 border border-[#18181B] rounded-md text-sm font-medium"
                      >
                        <Download className="w-4 h-4" /> {loadingFile ? "Apro…" : (m.filename || "Scarica PDF")}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {list.length === 0 && <div className="text-sm text-velta-mute">Nessun messaggio.</div>}
      </div>
    </div>
  );
}
